import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import API_URL from '../api';

export default function Portal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({ company_name: 'JobCostNow', brand_color: '#2E4057', logo_url: '', phone: '', address: '' });

  useEffect(() => {
    axios.get(`${API_URL}/api/jobs/portal/${token}`)
      .then(res => setData(res.data))
      .catch(() => setError('Job not found'));
    axios.get(`${API_URL}/api/settings`)
      .then(res => {
        if (res.data && res.data.company_name) {
          setSettings({
            company_name: res.data.company_name || 'JobCostNow',
            brand_color: res.data.brand_color || '#2E4057',
            logo_url: res.data.logo_url || '',
            phone: res.data.phone || '',
            address: res.data.address || ''
          });
        }
      })
      .catch(() => {});
  }, [token]);

  const fmt = (n) => '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
  const brand = settings.brand_color || '#2E4057';

  if (error) return <div style={{textAlign:'center',padding:'60px',color:'#c00'}}>{error}</div>;
  if (!data) return <div style={{textAlign:'center',padding:'60px'}}>Loading...</div>;

  return (
    <div style={styles.page}>
      <div style={{...styles.header, background: brand}} id="portal-header">
        {settings.logo_url
          ? <img src={settings.logo_url} alt={settings.company_name} style={styles.logoImg} onError={e => { e.target.style.display='none'; }} />
          : <h1 style={styles.logo}>{settings.company_name}</h1>}
        <p style={styles.subtitle}>{settings.company_name} &middot; Job Summary</p>
        {(settings.phone || settings.address) ? (
          <p style={styles.contactLine}>{[settings.address, settings.phone].filter(Boolean).join('  \u00b7  ')}</p>
        ) : null}
      </div>

      <div style={styles.printBar} className="no-print">
        <button onClick={() => window.print()} style={{...styles.printBtn, background: brand}}>Print / Save PDF</button>
      </div>

      <div style={styles.content} id="portal-doc">
        <div style={styles.card} className="pcard">
          <h2 style={{...styles.cardTitle, color: brand}}>Job Information</h2>
          <div style={styles.infoGrid}>
            <div><span style={styles.label}>Customer</span><span style={{...styles.value, color: brand}}>{data.customer_name}</span></div>
            <div><span style={styles.label}>Address</span><span style={{...styles.value, color: brand}}>{data.job_address || '\u2014'}</span></div>
            <div><span style={styles.label}>Job Number</span><span style={{...styles.value, color: brand}}>{data.job_number || '\u2014'}</span></div>
            <div><span style={styles.label}>Start Date</span><span style={{...styles.value, color: brand}}>{data.start_date ? new Date(data.start_date).toLocaleDateString() : '\u2014'}</span></div>
          </div>
        </div>

        <div style={styles.balanceGrid} className="pbal">
          <div style={{...styles.balanceCard, borderTop: `4px solid ${brand}`}} className="pbalcard"><div style={styles.balanceLabel}>Revised Contract</div><div style={{...styles.balanceValue, color: brand}}>{fmt(data.revised_contract)}</div></div>
          <div style={{...styles.balanceCard, borderTop: '4px solid #1E7145'}} className="pbalcard"><div style={styles.balanceLabel}>Total Paid</div><div style={{...styles.balanceValue, color: '#1E7145'}}>{fmt(data.total_paid)}</div></div>
          <div style={{...styles.balanceCard, borderTop: '4px solid #C0392B'}} className="pbalcard"><div style={styles.balanceLabel}>Balance Due</div><div style={{...styles.balanceValue, color: '#C0392B'}}>{fmt(data.balance_due)}</div></div>
        </div>

        <div style={styles.card} className="pcard">
          <h2 style={{...styles.cardTitle, color: brand}}>Change Orders</h2>
          {data.change_orders.length === 0 ? <p style={styles.empty}>No change orders</p> : (
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.th}>Amount</th><th style={styles.th}>Status</th></tr></thead>
              <tbody>{data.change_orders.map((co, i) => (
                <tr key={i} style={{background: i%2===0?'#fff':'#f9f9f9'}}>
                  <td style={styles.td}>{co.co_date ? new Date(co.co_date).toLocaleDateString() : '\u2014'}</td>
                  <td style={styles.td}>{co.description}</td>
                  <td style={styles.td}>{fmt(co.amount)}</td>
                  <td style={styles.td}><span style={{color: co.signed ? '#1E7145' : '#E67E22', fontWeight: 'bold'}}>{co.signed ? 'Signed' : 'Verbal Only'}</span></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>

        <div style={styles.card} className="pcard">
          <h2 style={{...styles.cardTitle, color: brand}}>Payment History</h2>
          {data.payments.length === 0 ? <p style={styles.empty}>No payments recorded</p> : (
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.th}>Method</th><th style={styles.th}>Amount</th></tr></thead>
              <tbody>{data.payments.map((p, i) => (
                <tr key={i} style={{background: i%2===0?'#fff':'#f9f9f9'}}>
                  <td style={styles.td}>{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '\u2014'}</td>
                  <td style={styles.td}>{p.description}</td>
                  <td style={styles.td}>{p.method || '\u2014'}</td>
                  <td style={{...styles.td, color: '#1E7145', fontWeight: 'bold'}}>{fmt(p.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>

        <div style={styles.footer} id="portal-footer">
          <p>{settings.company_name}{settings.phone ? '  \u00b7  ' + settings.phone : ''}</p>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 0.4in; }
          tr { page-break-inside: avoid; }
          #portal-doc { padding: 8px 0 0 !important; }
          #portal-header { padding: 12px 24px !important; }
          #portal-header img { max-height: 40px !important; }
          #portal-header h1 { font-size: 20px !important; }
          .pcard { padding: 12px 16px !important; margin-bottom: 10px !important; box-shadow: none !important; border: 1px solid #eceff2 !important; }
          .pcard h2 { font-size: 14px !important; margin-bottom: 8px !important; }
          .pbal { gap: 10px !important; margin-bottom: 10px !important; }
          .pbalcard { padding: 10px !important; box-shadow: none !important; border: 1px solid #eceff2 !important; }
          .pbalcard > div:first-child { font-size: 10px !important; margin-bottom: 2px !important; }
          .pbalcard > div:last-child { font-size: 18px !important; }
          #portal-doc table { font-size: 11.5px !important; }
          #portal-doc th, #portal-doc td { padding: 5px 8px !important; }
          #portal-footer { margin-top: 14px !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f4f6f9', fontFamily: 'Arial, sans-serif' },
  header: { background: '#2E4057', padding: '24px 32px', textAlign: 'center' },
  logo: { color: '#fff', margin: 0, fontSize: '28px' },
  logoImg: { maxHeight: '64px', maxWidth: '280px', objectFit: 'contain' },
  subtitle: { color: 'rgba(255,255,255,0.75)', margin: '8px 0 0', fontSize: '14px' },
  contactLine: { color: 'rgba(255,255,255,0.6)', margin: '4px 0 0', fontSize: '12px' },
  printBar: { maxWidth: '900px', margin: '0 auto', padding: '16px 16px 0', textAlign: 'right' },
  printBtn: { background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 18px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  content: { maxWidth: '900px', margin: '0 auto', padding: '16px 16px 32px' },
  card: { background: '#fff', borderRadius: '10px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  cardTitle: { margin: '0 0 16px 0', color: '#2E4057', fontSize: '18px' },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  label: { display: 'block', fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' },
  value: { fontSize: '15px', color: '#2E4057', fontWeight: '500' },
  balanceGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px', marginBottom: '20px' },
  balanceCard: { background: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' },
  balanceLabel: { fontSize: '12px', color: '#888', textTransform: 'uppercase', marginBottom: '8px' },
  balanceValue: { fontSize: '26px', fontWeight: 'bold', color: '#2E4057' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#888', textTransform: 'uppercase', background: '#f9f9f9' },
  td: { padding: '12px', fontSize: '14px', borderBottom: '1px solid #f0f0f0' },
  empty: { color: '#888', textAlign: 'center', padding: '20px' },
  footer: { textAlign: 'center', color: '#aaa', fontSize: '13px', marginTop: '32px' }
};
